import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Zap, FileText, Sparkles } from "lucide-react";
import { getPricingConfig, quoteEstimate } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

const ACCENT = "border-primary bg-primary/5";

function Chip({ active, onClick, children, sub, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-pressed={active}
      className={`text-left border px-4 py-3 transition-all active:scale-[0.98] ${
        active ? ACCENT + " shadow-sm" : "border-border bg-card hover:border-primary/50"
      }`}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">{children}</span>
        {active && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
      </span>
      {sub && <span className="block text-xs text-muted-foreground mt-0.5">{sub}</span>}
    </button>
  );
}

function StepTitle({ n, children }) {
  return (
    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
      <span className="text-primary">{String(n).padStart(2, "0")}</span> — {children}
    </p>
  );
}

/**
 * Instant Website Quote — config-driven (admin-editable), live estimate that
 * updates on every selection, currency-aware. The authoritative price is
 * recomputed server-side on submit; the live preview mirrors the same math.
 */
export default function QuoteCalculator({ compact = false }) {
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState({
    business_type: "small-business", size: "small", features: [],
    design: "professional",
  });
  const [submitting, setSubmitting] = useState(false);
  const [description, setDescription] = useState("");
  const navigate = useNavigate();
  const { fmt, code } = useCurrency();

  useEffect(() => { getPricingConfig().then(setCfg).catch(() => {}); }, []);

  const toggleFeature = (k) =>
    setSel((s) => ({ ...s, features: s.features.includes(k) ? s.features.filter((f) => f !== k) : [...s.features, k] }));

  // Live estimate — mirrors the server-side formula exactly.
  const live = useMemo(() => {
    if (!cfg) return null;
    const size = cfg.sizes.find((s) => s.key === sel.size) || cfg.sizes[0];
    const bt = cfg.business_types.find((b) => b.key === sel.business_type) || { adjust: 0 };
    const build = size.base + (bt.adjust || 0);
    const featuresTotal = sel.features.reduce((sum, fk) => sum + ((cfg.features.find((f) => f.key === fk) || {}).price || 0), 0);
    const seo = cfg.seo_setup_fee || 0;
    const total = Math.round(build + featuresTotal + seo);
    // Lease plan: care stack (hosting, security, backups, updates) + 6% of build/mo.
    const cc = cfg.care_config || {};
    const careStack = (cc.base_monthly || 0) + ((cc.per_size || {})[sel.size] || 0)
      + sel.features.reduce((sum, fk) => sum + ((cc.per_feature || {})[fk] || 0), 0);
    const leaseMonthly = Math.round(careStack + total * 0.06);
    const monthsCharged = cc.annual_months_charged || 10;
    return { build, featuresTotal, seo, total, leaseMonthly,
             leaseAnnual: leaseMonthly * monthsCharged,
             financeMonthly: Math.round(total / 12) };
  }, [cfg, sel]);

  if (!cfg) {
    return <div id="instant-quote" className="py-24 flex justify-center"><div className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin" /></div>;
  }

  const submit = async (mode) => {
    setSubmitting(true);
    try {
      const est = await quoteEstimate(sel);
      navigate("/start-project", { state: { estimate: est, selections: sel, mode } });
    } catch {
      navigate("/start-project", { state: { selections: sel, mode } });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="instant-quote" data-testid="quote-calculator" className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
      {/* CONFIGURATOR */}
      <div className="lg:col-span-7 space-y-9">
        <div>
          <StepTitle n={1}>What kind of business is it for?</StepTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {cfg.business_types.map((b) => (
              <Chip key={b.key} testId={`qc-type-${b.key}`} active={sel.business_type === b.key}
                onClick={() => setSel((s) => ({ ...s, business_type: b.key }))}>{b.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <StepTitle n={2}>How big is the website?</StepTitle>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {cfg.sizes.map((s) => (
              <Chip key={s.key} testId={`qc-size-${s.key}`} active={sel.size === s.key}
                sub={`from ${fmt(s.base)}`}
                onClick={() => setSel((x) => ({ ...x, size: s.key }))}>{s.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <StepTitle n={3}>Which features do you need?</StepTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {cfg.features.map((f) => (
              <Chip key={f.key} testId={`qc-feature-${f.key}`} active={sel.features.includes(f.key)}
                sub={f.price > 0 ? `+${fmt(f.price)}` : "Included"}
                onClick={() => toggleFeature(f.key)}>{f.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <StepTitle n={4}>Design style</StepTitle>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {cfg.designs.map((d) => (
              <Chip key={d.key} testId={`qc-design-${d.key}`} active={sel.design === d.key}
                sub="Included"
                onClick={() => setSel((s) => ({ ...s, design: d.key }))}>{d.label}</Chip>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Style is a preference, not a price — every design direction is included at no extra cost.</p>
        </div>

        <div>
          <StepTitle n={5}>Describe your project <span className="normal-case tracking-normal">(optional — powers your live preview)</span></StepTitle>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="qc-description"
            placeholder="e.g. A family-run plumbing company in Denver. We want online booking, reviews up front, and a trustworthy feel…"
            className="w-full bg-card border border-border px-4 py-3 text-sm leading-relaxed outline-none focus:ring-1 focus:ring-primary resize-y"
          />
        </div>
      </div>

      {/* LIVE ESTIMATE PANEL */}
      <div className="lg:col-span-5 lg:sticky lg:top-24">
        <motion.div layout className="border border-primary/30 bg-card soft-shadow-lg p-6 md:p-8" data-testid="quote-result">
          <p className="overline text-primary mb-6">Your Estimated Project</p>
          {live && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-border pb-3">
                <span className="text-muted-foreground">Website build</span>
                <span className="font-mono" data-testid="quote-build">{fmt(live.build)}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <span className="text-muted-foreground">Additional features{sel.features.length ? ` (${sel.features.length})` : ""}</span>
                <span className="font-mono" data-testid="quote-features">{fmt(live.featuresTotal)}</span>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <span className="text-muted-foreground">SEO &amp; launch setup</span>
                <span className="font-mono" data-testid="quote-seo">{fmt(live.seo)}</span>
              </div>
              <div className="flex justify-between items-end pt-2">
                <span className="font-medium">Total estimated project</span>
                <span className="font-display text-4xl md:text-5xl text-primary leading-none" data-testid="quote-total">{fmt(live.total)}</span>
              </div>
              {code !== "USD" && (
                <p className="text-xs text-muted-foreground text-right">≈ ${live.total.toLocaleString()} USD · live rate</p>
              )}
              {live.leaseMonthly > 0 && (
                <div className="border border-primary/30 bg-primary/5 p-4" data-testid="quote-lease">
                  <p className="text-sm font-medium">Free for 30 days — then <span className="text-primary">{fmt(live.leaseMonthly)}/month</span> or {fmt(live.leaseAnnual)}/year</p>
                  <p className="mt-1.5 text-xs text-muted-foreground">Your plan covers hosting, security, backups, monitoring, updates and care of everything we build. Optional 12-month financing spreads the build value into your plan. Cancel anytime.</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground border border-dashed border-border p-3 mt-2" data-testid="quote-no-deposit">
                No deposit, nothing due today — you sign a simple agreement, add billing details, and your first charge only happens 30 days after your site is live.
              </p>
            </div>
          )}

          <div className="mt-7 space-y-3">
            <button
              data-testid="quote-live-preview"
              onClick={() => navigate("/build", { state: { selections: sel, description, estimate: live ? { total: live.total, lease_monthly: live.leaseMonthly } : null, autorun: description.trim().length >= 10 } })}
              className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-background px-6 py-4 font-semibold hover:opacity-90 active:scale-[0.98] transition"
            >
              <Sparkles className="w-4 h-4" /> See a Live Preview of My Website
            </button>
            <button
              data-testid="quote-start-project"
              onClick={() => submit("deposit")}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 font-semibold hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-60"
            >
              <Zap className="w-4 h-4" /> {submitting ? "Preparing…" : "Start My Project"}
            </button>
            <button
              data-testid="quote-request-proposal"
              onClick={() => submit("proposal")}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 border border-border px-6 py-4 font-semibold hover:border-primary hover:text-primary active:scale-[0.98] transition disabled:opacity-60"
            >
              <FileText className="w-4 h-4" /> Request a Detailed Proposal
            </button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground leading-relaxed">{cfg.disclaimer}</p>
          {!compact && (
            <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
              <ArrowRight className="w-3.5 h-3.5 text-primary" /> No sales call needed to see pricing — ever.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
